import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getOfferDetails, getBuyerResponses } from '../../utils/api';
import { DashboardLayout } from '../../components/layout';
import '../dashboard/Dashboard.css';
import './OfferDetails.css';

const OfferDetails = () => {
  const { offerId } = useParams();
  const [timeFilter, setTimeFilter] = useState('This Week');
  const [offerData, setOfferData] = useState(null);
  const [buyerResponses, setBuyerResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOfferDetails();
  }, [offerId]);

  const loadOfferDetails = async () => {
    setLoading(true);
    try {
      const [offerResponse, buyersResponse] = await Promise.all([
        getOfferDetails(offerId),
        getBuyerResponses(offerId)
      ]);

      if (offerResponse.status === 200 && offerResponse.data) {
        setOfferData(offerResponse.data);
      } else {
        // Use sample data if API fails
        setOfferData(sampleOfferData);
      }

      if (buyersResponse.status === 200 && buyersResponse.data) {
        setBuyerResponses(buyersResponse.data.buyers || buyersResponse.data || []);
      } else {
        // Use sample data if API fails
        setBuyerResponses(sampleBuyerResponses);
      }
    } catch (error) {
      console.error('Error loading offer details:', error);
      // Use sample data as fallback
      setOfferData(sampleOfferData);
      setBuyerResponses(sampleBuyerResponses);
    } finally {
      setLoading(false);
    }
  };

  // Sample offer data
  const sampleOfferData = {
    totalQty: '1000 MT',
    moq: '150 MT',
    productName: 'PP Mould Borouge HD 168MO',
    originalPrice: '₹90.25 / KG',
    activeBids: '09/20',
    bidPercentage: '45.3%',
    offerTimeLeft: '1:12:98',
    rejectedBids: '31',
    rejectedPercentage: '8.0%'
  };

  // Sample buyer responses
  const sampleBuyerResponses = [
    { id: 1, buyerId: '2312', qty: '150 MT', price: '₹90.25', status: 'active' },
    { id: 2, buyerId: '1569', qty: '50 MT', price: '₹98.25', status: 'active' },
    { id: 3, buyerId: '3648', qty: '100 MT', price: '₹90.25', status: 'active' },
    { id: 4, buyerId: '5968', qty: '100 MT', price: '₹98.12', status: 'active' },
    { id: 5, buyerId: '0125', qty: '100 MT', price: '₹90.12', status: 'active' }
  ];

  // Sample chart data (mock data for the bar chart)
  const chartData = [
    { date: '08/01/24', stock: 800, price: 92.5 },
    { date: '09/01/24', stock: 950, price: 89.2 },
    { date: '02/01/24', stock: 600, price: 95.8 },
    { date: '01/01/24', stock: 700, price: 88.4 },
    { date: '01/03/24', stock: 450, price: 91.2 },
    { date: '31/12/23', stock: 850, price: 87.6 },
    { date: '30/12/23', stock: 920, price: 93.1 },
    { date: '29/12/23', stock: 780, price: 90.8 }
  ];

  // Breadcrumbs configuration
  const breadcrumbs = [
    { label: 'Sell Material', link: '/sell-material' },
    { label: '962345' }
  ];

  return (
    <DashboardLayout breadcrumbs={breadcrumbs}>
      <div className="offer-details-section">
        <h2 className="section-title">Offer Details</h2>

        <div className="offer-stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-weight-hanging"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{offerData?.totalQty || 'Loading...'}</div>
              <div className="stat-label">Total QTY</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-layer-group"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{offerData?.moq || 'Loading...'}</div>
              <div className="stat-label">MOQ</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-box"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{offerData?.productName || 'Loading...'}</div>
              <div className="stat-label">Product Name</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-tag"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{offerData?.originalPrice || 'Loading...'}</div>
              <div className="stat-label">Original Price</div>
            </div>
          </div>
        </div>

        <div className="bids-placed-section">
          <h3 className="section-subtitle">Bids Placed on Offer</h3>

          <div className="bids-stats-grid">
            <div className="bid-stat-card">
              <div className="bid-stat-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="bid-stat-content">
                <div className="bid-stat-value">{offerData?.activeBids || 'Loading...'}</div>
                <div className="bid-stat-percentage">↗ {offerData?.bidPercentage || '0%'}</div>
                <div className="bid-stat-label">Active Total Bids</div>
              </div>
            </div>

            <div className="bid-stat-card">
              <div className="bid-stat-icon">
                <i className="fas fa-clock"></i>
              </div>
              <div className="bid-stat-content">
                <div className="bid-stat-value">{offerData?.offerTimeLeft || 'Loading...'}</div>
                <div className="bid-stat-label">Offer Time Left</div>
              </div>
            </div>

            <div className="bid-stat-card">
              <div className="bid-stat-icon">
                <i className="fas fa-times-circle"></i>
              </div>
              <div className="bid-stat-content">
                <div className="bid-stat-value">{offerData?.rejectedBids || 'Loading...'}</div>
                <div className="bid-stat-percentage">↘ {offerData?.rejectedPercentage || '0%'}</div>
                <div className="bid-stat-label">Rejected Bids</div>
              </div>
            </div>
          </div>
        </div>

        <div className="buyer-responses-section">
          <h3 className="section-subtitle">All Buyers Responses ({buyerResponses.length})</h3>

          {loading ? (
            <div className="loading-message">Loading buyer responses...</div>
          ) : (
            <div className="table-wrapper">
              <table className="buyer-responses-table">
                <thead>
                  <tr>
                    <th>Buyer ID</th>
                    <th>QTY (MT)</th>
                    <th>Price/KG</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {buyerResponses.map((buyer) => (
                    <tr key={buyer.id}>
                      <td>
                        <div className="buyer-id">
                          <i className="fas fa-user-circle"></i>
                          <span>Buyer {buyer.id}</span>
                          <span className="buyer-number">{buyer.buyerId}</span>
                        </div>
                      </td>
                      <td>{buyer.qty}</td>
                      <td>{buyer.price}</td>
                      <td>
                        <button className="action-btn view-btn">
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="price-history-section">
          <div className="price-history-header">
            <h3 className="section-subtitle">Stocks & Price History</h3>
            <select
              className="time-filter"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
            >
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="Last 3 Months">Last 3 Months</option>
              <option value="This Year">This Year</option>
            </select>
          </div>

          <div className="chart-container">
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color stock-color"></div>
                <span>Stock of Material: 600MT</span>
              </div>
              <div className="legend-item">
                <div className="legend-color price-color"></div>
                <span>Price (₹/kg): ₹92.14</span>
              </div>
            </div>

            <div className="chart-area">
              <div className="chart-y-axis">
                <span>Material Price (₹)</span>
              </div>
              <div className="chart-bars">
                {chartData.map((data, index) => (
                  <div key={index} className="chart-bar-group">
                    <div
                      className="chart-bar stock-bar"
                      style={{ height: `${(data.stock / 1000) * 100}%` }}
                    ></div>
                    <div className="chart-date">{data.date}</div>
                  </div>
                ))}
              </div>
              <div className="chart-line">
                {/* Price line would be implemented with SVG or Canvas in a real app */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OfferDetails;